import { getApiBaseUrl } from "../config/runtimeConfig";
import { invalidateLookup } from "../lookups/lookupsApi";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export interface Supplier {
  id: number;
  name: string;
  taxId?: string | null;
  category?: string | null;
  contactName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  commercialTerms?: string | null;
  purchasesCount: number;
  notes?: string | null;
  logoUrl?: string | null;
  created: string;
  lastModified: string;
}

export interface SupplierPayload {
  name: string;
  taxId?: string | null;
  category?: string | null;
  contactName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  commercialTerms?: string | null;
  purchasesCount?: number;
  notes?: string | null;
}

export interface SupplierLogoResponse {
  supplierId: number;
  logoUrl?: string | null;
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      text || response.statusText,
    );
  }

  return (await response.json()) as T;
}

export function getSuppliers(): Promise<Supplier[]> {
  return request<Supplier[]>("/api/suppliers");
}

export function getSupplierById(supplierId: number): Promise<Supplier> {
  return request<Supplier>(`/api/suppliers/${supplierId}`);
}

export function createSupplier(
  payload: SupplierPayload,
): Promise<Supplier> {
  return request<Supplier>("/api/suppliers", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  }).then((result) => {
    invalidateLookup("suppliers");
    return result;
  });
}

export function updateSupplier(
  supplierId: number,
  payload: SupplierPayload,
): Promise<Supplier> {
  return request<Supplier>(`/api/suppliers/${supplierId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  }).then((result) => {
    invalidateLookup("suppliers");
    return result;
  });
}

export function uploadSupplierLogo(
  supplierId: number,
  file: File,
): Promise<SupplierLogoResponse> {
  const formData = new FormData();
  formData.append("logo", file, file.name || "logo");

  return request<SupplierLogoResponse>(
    `/api/suppliers/${supplierId}/logo`,
    {
      method: "POST",
      body: formData,
    },
  );
}

// --- Duplicate suppliers (Lote A-22 [2.1.13]) ---

export interface DuplicateSupplierGroup {
  matchType: "tax_id" | "name";
  key: string;
  suppliers: Supplier[];
}

export function getDuplicateSuppliers(): Promise<DuplicateSupplierGroup[]> {
  return request<DuplicateSupplierGroup[]>("/api/suppliers/duplicates");
}

export function mergeSuppliers(
  survivorId: number,
  loserIds: number[],
): Promise<Supplier> {
  return request<Supplier>(`/api/suppliers/${survivorId}/merge`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ loserIds }),
  }).then((result) => {
    invalidateLookup("suppliers");
    return result;
  });
}
